import { Link as RouterLink } from 'react-router';
import MenuList from '@mui/material/MenuList';
import { Typography } from '@components/Typography';
import { useTranslation } from '@hooks';
import { LogoutIcon } from 'assets/images/sidebar';
import { PopoverHeader, ProfileMenuPopover, ProfileMenuItem, ProfileMenuDivider } from './styles';

export const PROFILE_MENU_ID = 'pl-menu-profile';

const linkProps = ({ external, href, to }) =>
    external ? { component: 'a', href, target: '_blank', rel: 'noopener' } : { component: RouterLink, to };

const useProfileMenuRows = ({ items, accountSwitcher, onClose, onLogout }) => {
    const { t } = useTranslation();
    const { accounts, activeAccountId, selectAccount } = accountSwitcher;

    const onSelectAccount = async accountId => {
        if (accountId === activeAccountId) {
            onClose();
            return;
        }
        try {
            await selectAccount(accountId);
        } catch {
            onClose();
        }
    };

    const onClickLogout = () => {
        onClose();
        onLogout();
    };

    const accountRows =
        accounts.length > 1
            ? [
                  <PopoverHeader key="switch-account" muiSkipListHighlight>
                      {t('Sidebar.switchAccount')}
                  </PopoverHeader>,
                  ...accounts.map(({ id, name }) => (
                      <ProfileMenuItem key={id} onClick={() => onSelectAccount(id)} selected={id === activeAccountId}>
                          <Typography variant="body2">{name}</Typography>
                      </ProfileMenuItem>
                  )),
                  <ProfileMenuDivider key="switch-account-divider" muiSkipListHighlight />,
              ]
            : [];

    return [
        ...accountRows,
        ...items.map(item => (
            <ProfileMenuItem key={item.id} id={item.id} withIcon onClick={onClose} {...linkProps(item)}>
                {item.icon}
                <Typography variant="body2">{item.title}</Typography>
            </ProfileMenuItem>
        )),
        <ProfileMenuDivider key="logout-divider" muiSkipListHighlight />,
        <ProfileMenuItem key="logout" id="pl-menu-profile-logout" withIcon onClick={onClickLogout}>
            <LogoutIcon />
            <Typography variant="body2">{t('Sidebar.logout')}</Typography>
        </ProfileMenuItem>,
    ];
};

export const CollapsedProfileMenu = ({ id, autoFocusItem, ...rowProps }) => (
    <MenuList id={id} autoFocusItem={autoFocusItem} disablePadding>
        {useProfileMenuRows(rowProps)}
    </MenuList>
);

const ProfileMenu = ({ anchorEl, onClose, ...rowProps }) => (
    <ProfileMenuPopover
        id={PROFILE_MENU_ID}
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={onClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { id: 'pl-menu-top-list' } }}
    >
        {useProfileMenuRows({ onClose, ...rowProps })}
    </ProfileMenuPopover>
);

export default ProfileMenu;
